import { FileText } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { EDUCATION_LEVEL_LABELS, PANELIST_ROLE_LABELS, RECOMMENDATION_LABELS, type CockpitView, type PriorRoundView } from '@nest-recruit/shared';
import { Button } from '../../../components/Button';
import { Card } from '../../../components/Card';
import { Chip } from '../../../components/Chip';
import { CvViewer } from '../../../components/cv/CvViewer';
import { RECOMMENDATION_TONES, RESULT_TONES } from '../../../components/status-tones';
import { ToneChip } from '../../../components/Tone';
import { EMPTY, formatDate } from '../../../lib/format';
import styles from '../pages/Cockpit.module.css';

const MONTHS_PER_YEAR = 12;
/** Cockpit.module.css's breakpoint below which the cockpit shows one side at a time. */
const ONE_SIDE_AT_A_TIME = '(max-width: 1180px)';


/**
 * INT-01's left column (E9): the candidate summary, the CV (one click), the test result and
 * what earlier rounds said. Contact details never reach it (§L23: panelists see the name).
 */
export function CandidateColumn({ view }: { readonly view: Pick<CockpitView, 'candidate' | 'assessment' | 'cv' | 'prior'> }): ReactNode {
  // Redesign Group 7 (Scorecard mockups): beside the scorecard on a computer the CV is simply there;
  // on a phone the candidate side is its own tab, and a CV filling it would bury the summary.
  const [isCvOpen, setIsCvOpen] = useState(() => !window.matchMedia(ONE_SIDE_AT_A_TIME).matches);
  const { candidate, assessment, cv, prior } = view;
  const role = [candidate.currentTitle, candidate.currentEmployer].filter(Boolean).join(' at ');

  return (
    <div className={styles.column}>
      <Card title={candidate.fullName} meta={`Application ${candidate.applicationReference}`}>
        {candidate.isInternal && (
          <div className={styles.chips}>
            <Chip tone="attention">Internal applicant</Chip>
          </div>
        )}
        <dl className={styles.facts}>
          <dt>Now</dt>
          <dd>{role || EMPTY}</dd>
          <dt>Experience</dt>
          <dd>{experience(candidate.experienceMonths)}</dd>
          <dt>Education</dt>
          <dd>{candidate.highestEducation ? EDUCATION_LEVEL_LABELS[candidate.highestEducation] : EMPTY}</dd>
          <dt>City</dt>
          <dd>{candidate.city ?? EMPTY}</dd>
          <dt>Match</dt>
          <dd>{candidate.matchScore === null ? EMPTY : `${candidate.matchScore} / 100`}</dd>
        </dl>
      </Card>

      <Card
        title="CV"
        meta={cv ? cv.fileName : 'None on this application'}
        action={
          cv && !cv.isRemoved ? (
            <Button size="sm" variant="outline" icon={FileText} onClick={() => setIsCvOpen((open) => !open)} aria-expanded={isCvOpen}>
              {isCvOpen ? 'Hide CV' : 'Open CV'}
            </Button>
          ) : undefined
        }
      >
        {isCvOpen && (
          <div className={styles.cv}>
            <CvViewer cv={cv} candidateName={candidate.fullName} fallback="Score from the interview itself." />
          </div>
        )}
        {cv?.isRemoved && <p className={styles.quiet}>This CV was removed under the retention policy.</p>}
      </Card>

      <Card title="Test result">
        {assessment ? (
          <dl className={styles.facts}>
            <dt>Test</dt>
            <dd>{assessment.testName}</dd>
            <dt>Score</dt>
            <dd>
              {assessment.scorePercent === null ? EMPTY : `${Math.round(assessment.scorePercent)}%`}
              {assessment.passPercent !== null ? ` · pass mark ${assessment.passPercent}%` : ''}
              {assessment.isPassed !== null && (
                <>
                  {' '}
                  <ToneChip tone={assessment.isPassed ? RESULT_TONES.passed : RESULT_TONES.below}>{assessment.isPassed ? 'Passed' : 'Not passed'}</ToneChip>
                </>
              )}
            </dd>
          </dl>
        ) : (
          <p className={styles.quiet}>No test on this application.</p>
        )}
      </Card>

      <PriorFeedback prior={prior} />
    </div>
  );
}

/** F11.02 — earlier rounds' calls, scores and summary notes, or a plain word on why not. */
function PriorFeedback({ prior }: { readonly prior: readonly PriorRoundView[] | null }): ReactNode {
  if (prior === null) {
    return (
      <Card title="Earlier rounds">
        <p className={styles.quiet}>This round is set not to show earlier rounds' feedback, so you score with fresh eyes.</p>
      </Card>
    );
  }
  if (prior.length === 0) return null;
  return (
    <>
      {prior.map((round) => (
        <Card key={round.roundNumber} title={`Round ${round.roundNumber} — ${round.roundName}`} meta={round.interviewedAt ? formatDate(round.interviewedAt) : undefined}>
          <ul className={styles.feedback}>
            {round.feedback.map((item) => (
              <li key={`${item.panelistName}-${item.submittedAt}`} className={styles.feedbackItem}>
                <div className={styles.feedbackHead}>
                  <span className={styles.name}>{item.panelistName}</span>
                  {item.role !== 'counting' && (
                    <Chip tone="neutral" plain>
                      {PANELIST_ROLE_LABELS[item.role]}
                    </Chip>
                  )}
                  {item.recommendation && <ToneChip tone={RECOMMENDATION_TONES[item.recommendation]}>{RECOMMENDATION_LABELS[item.recommendation]}</ToneChip>}
                  <span className={styles.score}>{item.panelistScore === null ? EMPTY : item.panelistScore.toFixed(2)}</span>
                </div>
                <p className={styles.notes}>{item.notes ?? EMPTY}</p>
              </li>
            ))}
          </ul>
          {round.missing.length > 0 && <p className={styles.quiet}>No scorecard from {round.missing.join(', ')}.</p>}
        </Card>
      ))}
    </>
  );
}

function experience(months: number | null): string {
  if (months === null) return EMPTY;
  const years = Math.floor(months / MONTHS_PER_YEAR);
  const rest = months % MONTHS_PER_YEAR;
  const parts = [years > 0 ? `${years} yr${years === 1 ? '' : 's'}` : '', rest > 0 ? `${rest} mo` : ''].filter(Boolean);
  return parts.length > 0 ? parts.join(' ') : 'Under a month';
}
